import React, { useState } from "react";
import PropTypes from "prop-types";

import { DataGrid, Toolbar } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { isEmpty } from "utils/validationUtil";
import { Box, Button, IconButton } from "@mui/material";
import { Edit, Add as AddIcon } from "components/icons";
import { useResizableColumnWidths } from "app/hooks/useResizableColumnWidths";
import { DEFAULT_TABLE_PAGE_SIZE } from "AppConstants";

/**
 * Add button in the grid's own toolbar, matching the Departments, Users and Clients grids.
 * The border under this strip is the DataGrid toolbar's, not the button's.
 *
 * @since NEXT_VERSION
 */
export const ClientProjectsGridToolbar = ({ onAddProject }) => (
  <Toolbar>
    <Box
      sx={{
        py: 2.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        width: "100%",
      }}
    >
      {onAddProject && (
        <Button
          startIcon={<AddIcon />}
          onClick={onAddProject}
          sx={{ width: 200, whiteSpace: "nowrap" }}
          variant="contained"
          color="success"
        >
          Add Client Project
        </Button>
      )}
    </Box>
  </Toolbar>
);

ClientProjectsGridToolbar.propTypes = {
  onAddProject: PropTypes.func,
};

/**
 * Master data grid for client projects, modelled on ActivityTypesDataGrid.
 *
 * Deactivated projects stay listed rather than disappearing, because the hours logged against
 * them still show in reports. The Active column is how an administrator tells the two apart,
 * and Edit is where the flag is turned on or off.
 *
 * @since NEXT_VERSION
 */
export const ClientProjectsDataGrid = (props) => {
  const { projects } = props;
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton
          aria-label="Edit"
          onClick={() => props.editEventHandler(obj.row)}
          color="inherit"
        >
          <Edit />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "Id", width: 120 },
    { field: "code", headerName: "Code", width: 120 },
    { field: "name", headerName: "Project Name", width: 320 },
    {
      field: "Client",
      headerName: "Client Name",
      width: 260,
      valueGetter: (value, row) => row.client?.name ?? "",
    },
    {
      field: "active",
      headerName: "Active",
      width: 100,
      valueGetter: (value, row) => (row.active ? "Yes" : "No"),
    },
    { field: "displayOrder", headerName: "Order", width: 90 },
  ];

  // Keeps a dragged column width alive across the re-render that editing a row causes.
  const { columns: sizedColumns, onColumnWidthChange } = useResizableColumnWidths(columns);

  const getData = () => {
    if (isEmpty(projects) || projects.length === 0) return [];
    return projects;
  };

  return (
    <MDBox py={1.5} px={2} height={500}>
      <DataGrid
        density="compact"
        rows={getData()}
        columns={sizedColumns}
        onColumnWidthChange={onColumnWidthChange}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20]}
        loading={props.isLoading}
        // Hidden by default like the other Master Data grids' ids (the old `hide: true` column
        // flag no longer exists in DataGrid v8, so the column showed).
        initialState={{
            columns: { columnVisibilityModel: { id: false } },
            pagination: { paginationModel: { pageSize: DEFAULT_TABLE_PAGE_SIZE } },
        }}
        showToolbar
        slots={{ toolbar: ClientProjectsGridToolbar }}
        slotProps={{ toolbar: { onAddProject: props.onAddProject } }}
      />
    </MDBox>
  );
};

ClientProjectsDataGrid.propTypes = {
  projects: PropTypes.array,
  isLoading: PropTypes.bool,
  onAddProject: PropTypes.func,
  editEventHandler: PropTypes.func.isRequired,
};
